import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

function useCopy() {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(id);
  }, [copied]);
  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(() => setCopied(true), () => undefined);
  };
  return { copied, copy };
}

/** One-line value with a copy button, e.g. the MCP server address. */
export function CopyField({ value, label, tone = 'light', className }: { value: string; label?: string; tone?: 'light' | 'dark'; className?: string }) {
  const { copied, copy } = useCopy();
  const dark = tone === 'dark';
  return (
    <div className={cn('flex max-w-full items-center gap-2 rounded-xl border py-1.5 pl-3.5 pr-1.5', dark ? 'border-white/15 bg-white/[0.06]' : 'border-bv-blue-line bg-white shadow-bv-card', className)}>
      {label && <span className={cn('shrink-0 text-[11px] font-semibold uppercase tracking-wider', dark ? 'text-white/50' : 'text-bv-mute')}>{label}</span>}
      <code className={cn('min-w-0 flex-1 truncate font-mono text-[13.5px]', dark ? 'text-white' : 'text-bv-ink')}>{value}</code>
      <button type="button" onClick={() => copy(value)} aria-label={copied ? 'Copied' : `Copy ${value}`} className={cn('flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue', dark ? 'bg-white text-bv-ink hover:bg-white/90' : 'bg-bv-ink text-white hover:bg-bv-ink/90')}>
        {copied ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** A prompt a recruiter can paste into their assistant. */
export function PromptCard({ prompt }: { prompt: string }) {
  const { copied, copy } = useCopy();
  return (
    <button type="button" onClick={() => copy(prompt)} className="group flex w-full items-start gap-3 rounded-2xl border border-bv-blue-line bg-white p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-bv-blue/40 hover:shadow-bv-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
      <span className="min-w-0 flex-1 text-[15px] leading-6 text-bv-ink">{prompt}</span>
      <span className={cn('mt-0.5 flex h-7 shrink-0 items-center gap-1 rounded-lg px-2 text-[11px] font-semibold transition-colors', copied ? 'bg-bv-success-soft text-bv-success' : 'bg-bv-blue-wash text-bv-blue group-hover:bg-bv-ink group-hover:text-white')}>
        {copied ? <Check className="h-3 w-3" strokeWidth={3} /> : <Copy className="h-3 w-3" />}
        <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
      </span>
    </button>
  );
}
